<template>
  <van-nav-bar
    title="账号"
    left-text="返回"
    left-arrow
    @click-left="onClickLeft"
    fixed
    placeholder
  ></van-nav-bar>

  <div class="account">
    <!-- 当前登录的账号 -->
    <van-cell-group inset>
      <van-cell title="账号" :value="account" />
      <van-cell title="角色" :value="currentRoleText" />
      <van-cell title="修改密码" is-link @click="toResetPwd">
        <template #icon>
          <van-icon name="lock" class="menu-icon" color="#607d8b" />
        </template>
      </van-cell>
    </van-cell-group>

    <!-- 保存入口：手动点才存，不自动保存 -->
    <div class="save-wrap">
      <van-button
        size="small"
        plain
        :color="saved ? '#c8c9cc' : '#52c1b2'"
        :disabled="saved"
        @click="onSave"
        >{{ saved ? "已保存" : "保存此账号" }}</van-button
      >
    </div>

    <!-- 已保存账号：点条目直接免密切换，右侧 ✕ 删除 -->
    <div class="section-title" v-if="list.length">已保存账号（点击切换）</div>
    <van-cell-group inset v-if="list.length">
      <van-cell v-for="it in list" :key="it.userNo" clickable @click="switchTo(it)">
        <template #title>
          <span class="acc-no">{{ it.userNo }}</span>
          <van-tag class="acc-tag" plain size="medium" :type="tagType(it.role)">{{
            roleTextOf(it.role)
          }}</van-tag>
          <van-tag
            class="acc-tag"
            v-if="it.userNo === account"
            type="success"
            plain
            size="medium"
            >当前</van-tag
          >
        </template>
        <template #label>
          <span class="acc-sub">{{ subText(it) }}</span>
        </template>
        <template #right-icon>
          <van-icon name="cross" class="acc-del" @click.stop="onRemove(it)" />
        </template>
      </van-cell>
    </van-cell-group>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";
import { decryption } from "../common/js/utils";
import { applyLoginResult, ROLE_TEXT } from "../utils/auth";
import {
  getSavedAccounts,
  saveAccount,
  removeAccount,
  isSaved,
} from "../utils/accountStore";

export default {
  setup() {
    const router = useRouter();
    const account = ref("");
    const currentRoleText = ref("");
    const saved = ref(false);
    const list = ref([]);
    const switching = ref(false); // 切换中：防重复点击

    const onClickLeft = () => {
      router.go(-1);
    };

    const toResetPwd = () => router.push({ path: "/resetpwd" });

    const roleTextOf = (role) => ROLE_TEXT[Number(role)] || "未知";

    const tagType = (role) => (Number(role) === 1 ? "primary" : "success");

    const subText = (it) => {
      const parts = [];
      if (it.name) parts.push(it.name);
      if (it.schoolName) parts.push(it.schoolName);
      return parts.join(" · ") || "—";
    };

    const refresh = () => {
      account.value =
        sessionStorage.getItem("userNo") || localStorage.getItem("userNo") || "";
      currentRoleText.value = roleTextOf(sessionStorage.getItem("role"));
      saved.value = !!account.value && isSaved(account.value);
      list.value = getSavedAccounts();
    };

    // 保存当前账号：密码取"记住密码"存的那份，没有则取本次登录缓存的那份
    const onSave = () => {
      const userNo = account.value;
      const pwd = localStorage.getItem("pwd") || sessionStorage.getItem("curPwdEnc");
      if (!userNo || !pwd) {
        Toast("请重新登录后再保存");
        return;
      }
      saveAccount({
        userNo,
        pwd,
        role: sessionStorage.getItem("role"),
        name: sessionStorage.getItem("userName") || "",
        schoolName:
          sessionStorage.getItem("tSchoolName") || sessionStorage.getItem("schoolName") || "",
      });
      Toast("已保存");
      refresh();
    };

    // 删除已保存账号（破坏性操作，二次确认）
    const onRemove = async (it) => {
      try {
        await Dialog.confirm({
          title: "移除账号",
          message: `确定移除已保存的账号 ${it.userNo} 吗？`,
        });
      } catch (e) {
        return; // 用户取消
      }
      removeAccount(it.userNo);
      Toast("已移除");
      refresh();
    };

    /**
     * 切换账号：用保存的账号密码重放一次登录，拿到新 sid 再重写本地身份。
     * 等价于"手动版的 reLogin"（service.js 里 sid 失效时走的是同一条路）。
     */
    const switchTo = async (it) => {
      if (switching.value) return;
      if (it.userNo === account.value) {
        Toast("已是当前账号");
        return;
      }
      const openId = localStorage.getItem("openId");
      if (!openId) {
        Toast("缺少身份信息，请重新进入");
        return;
      }
      switching.value = true;
      Toast.loading({ message: "切换中...", forbidClick: true, duration: 0 });
      try {
        const res = await api.login({
          user_no: it.userNo,
          password: decryption(it.pwd),
          openID: openId,
        });
        if (res.data.code == 1001) {
          const r = applyLoginResult(res.data.result, res.data.result.sid);
          if (!r.ok) {
            Toast(r.msg || "切换失败");
            return;
          }
          // 同步重登凭据：否则将来 sid 失效时 service.js 会把上一个账号登回来
          localStorage.setItem("userNo", it.userNo);
          localStorage.setItem("pwd", it.pwd);
          await router.replace(r.path);
        } else {
          // 密码改过或账号停用：留着只会一直失败，直接移除
          Toast(res.data.message || "密码已失效，请重新登录");
          removeAccount(it.userNo);
          refresh();
        }
      } catch (e) {
        Toast("切换失败，请重试");
      } finally {
        Toast.clear();
        switching.value = false;
      }
    };

    onMounted(() => {
      refresh();
    });

    return {
      account,
      currentRoleText,
      saved,
      list,
      onClickLeft,
      toResetPwd,
      roleTextOf,
      tagType,
      subText,
      onSave,
      onRemove,
      switchTo,
    };
  },
};
</script>

<style scoped>
.account {
  padding-top: 10px;
}
.menu-icon {
  margin-right: 8px;
  font-size: 20px;
}
.save-wrap {
  padding: 16px;
  text-align: center;
}
.section-title {
  padding: 16px 16px 8px;
  font-size: 12px;
  color: #999;
}
.acc-no {
  font-size: 14px;
  color: #333;
}
.acc-tag {
  margin-left: 8px;
}
.acc-sub {
  font-size: 12px;
  color: #999;
}
.acc-del {
  margin-left: 12px;
  font-size: 16px;
  color: #ccc;
}
</style>
